import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Play, Pause, Volume2, VolumeX, BookOpen, Lightbulb, CheckCircle, ChevronRight, Star } from 'lucide-react';
import { Card } from '../components/Card';
interface ImmersiveModuleProps {
  onBack: () => void;
}
// Audio helper
function playNote(freq: number, duration: number = 0.5) {
  const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
  const osc = audioContext.createOscillator();
  const gain = audioContext.createGain();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(freq, audioContext.currentTime);
  osc.connect(gain);
  gain.connect(audioContext.destination);
  gain.gain.setValueAtTime(0.3, audioContext.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration);
  osc.start(audioContext.currentTime);
  osc.stop(audioContext.currentTime + duration);
}
export function ImmersiveModule({
  onBack
}: ImmersiveModuleProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const lessonSteps = [{
    type: 'learn',
    title: '什么是音高？',
    icon: '🎵',
    content: {
      definition: '音高是指声音的高低程度，由声波振动的频率决定。',
      keyPoints: ['振动越快（频率越高），声音越高', '振动越慢（频率越低），声音越低', '音高用 Do Re Mi Fa Sol La Si 来表示'],
      example: '想象一下：小鸟叫声很高，大象叫声很低，这就是音高的区别！',
      visual: '🐦 高音 ↔️ 🐘 低音'
    }
  }, {
    type: 'interactive',
    title: '听一听，比一比',
    icon: '👂',
    content: {
      instruction: '点击下面的按钮，听听不同音高的声音',
      notes: [{
        name: 'Do (低)',
        freq: 261.63,
        color: 'bg-red-100 text-red-700 border-red-200'
      }, {
        name: 'Mi (中)',
        freq: 329.63,
        color: 'bg-yellow-100 text-yellow-700 border-yellow-200'
      }, {
        name: 'Sol (高)',
        freq: 392,
        color: 'bg-green-100 text-green-700 border-green-200'
      }, {
        name: 'Do (更高)',
        freq: 523.25,
        color: 'bg-blue-100 text-blue-700 border-blue-200'
      }]
    }
  }, {
    type: 'quiz',
    title: '小测验：哪个音更高？',
    icon: '❓',
    content: {
      question: '听完两个音后，判断哪个音更高？',
      audioA: 261.63,
      audioB: 523.25,
      options: ['第一个音更高', '第二个音更高', '一样高'],
      correctAnswer: 1,
      explanation: '第二个音的频率是第一个音的两倍，所以听起来更高！这两个音相差一个"八度"。'
    }
  }, {
    type: 'practice',
    title: '音阶练习',
    icon: '🎹',
    content: {
      instruction: '按顺序点击，演奏一个完整的音阶 Do Re Mi Fa Sol La Si Do',
      scale: [{
        name: 'Do',
        freq: 261.63
      }, {
        name: 'Re',
        freq: 293.66
      }, {
        name: 'Mi',
        freq: 329.63
      }, {
        name: 'Fa',
        freq: 349.23
      }, {
        name: 'Sol',
        freq: 392
      }, {
        name: 'La',
        freq: 440
      }, {
        name: 'Si',
        freq: 493.88
      }, {
        name: 'Do',
        freq: 523.25
      }]
    }
  }, {
    type: 'summary',
    title: '知识总结',
    icon: '📝',
    content: {
      points: [{
        emoji: '🎵',
        text: '音高是声音的高低，由振动频率决定'
      }, {
        emoji: '📈',
        text: '频率越高，音越高；频率越低，音越低'
      }, {
        emoji: '🎼',
        text: '音阶从低到高：Do Re Mi Fa Sol La Si Do'
      }, {
        emoji: '🎯',
        text: '相差一个八度的两个音，频率相差一倍'
      }]
    }
  }];
  const currentLesson = lessonSteps[currentStep];
  const handleQuizAnswer = (answerIndex: number) => {
    setSelectedAnswer(answerIndex);
    setShowResult(true);
    if (answerIndex === (currentLesson.content as any).correctAnswer) {
      setScore(s => s + 10);
    }
  };
  const handleNextStep = () => {
    if (!completedSteps.includes(currentStep)) {
      setCompletedSteps([...completedSteps, currentStep]);
    }
    setSelectedAnswer(null);
    setShowResult(false);
    if (currentStep < lessonSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };
  const renderStepContent = () => {
    switch (currentLesson.type) {
      case 'learn':
        const learnContent = currentLesson.content as any;
        return <div className="space-y-6" data-id="element-284">
            <Card className="p-6 bg-blue-50 border-blue-100" data-id="element-285">
              <div className="flex items-start space-x-4" data-id="element-286">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center text-2xl flex-shrink-0" data-id="element-287">
                  💡
                </div>
                <div data-id="element-288">
                  <h4 className="font-bold text-blue-800 mb-2" data-id="element-289">定义</h4>
                  <p className="text-blue-700" data-id="element-290">{learnContent.definition}</p>
                </div>
              </div>
            </Card>

            <Card className="p-6 bg-white" data-id="element-291">
              <h4 className="font-bold text-slate-800 mb-4 flex items-center" data-id="element-292">
                <Lightbulb className="mr-2 text-amber-500" size={20} data-id="element-293" />
                重要知识点
              </h4>
              <div className="space-y-3" data-id="element-294">
                {learnContent.keyPoints.map((point: string, i: number) => <motion.div key={i} initial={{
                opacity: 0,
                x: -20
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: i * 0.2
              }} className="flex items-center space-x-3 p-3 bg-slate-50 rounded-lg" data-id="element-295">
                    <CheckCircle className="text-green-500 flex-shrink-0" size={20} data-id="element-296" />
                    <span className="text-slate-700" data-id="element-297">{point}</span>
                  </motion.div>)}
              </div>
            </Card>

            <Card className="p-6 bg-amber-50 border-amber-100" data-id="element-298">
              <h4 className="font-bold text-amber-800 mb-2" data-id="element-299">🌟 生活中的例子</h4>
              <p className="text-amber-700 mb-4" data-id="element-300">{learnContent.example}</p>
              <div className="text-center text-3xl py-4 bg-white rounded-xl" data-id="element-301">
                {learnContent.visual}
              </div>
            </Card>
          </div>;
      case 'interactive':
        const interactiveContent = currentLesson.content as any;
        return <div className="space-y-6" data-id="element-302">
            <Card className="p-6 bg-purple-50 border-purple-100" data-id="element-303">
              <p className="text-purple-700 font-medium text-center" data-id="element-304">
                {interactiveContent.instruction}
              </p>
            </Card>

            <div className="grid grid-cols-2 gap-4" data-id="element-305">
              {interactiveContent.notes.map((note: any, i: number) => <motion.button key={i} whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.95
            }} onClick={() => playNote(note.freq, 0.8)} className={`p-6 rounded-2xl border-2 ${note.color} font-bold text-lg transition-all hover:shadow-lg`} data-id="element-306">
                  <div className="text-4xl mb-2" data-id="element-307">🔊</div>
                  {note.name}
                </motion.button>)}
            </div>

            <Card className="p-4 bg-slate-50" data-id="element-308">
              <p className="text-slate-600 text-sm text-center" data-id="element-309">
                💡 提示：从左到右，从上到下，音高逐渐升高
              </p>
            </Card>
          </div>;
      case 'quiz':
        const quizContent = currentLesson.content as any;
        return <div className="space-y-6" data-id="element-310">
            <Card className="p-6 bg-white" data-id="element-311">
              <p className="text-slate-700 font-medium text-center mb-6" data-id="element-312">
                {quizContent.question}
              </p>

              <div className="flex justify-center space-x-4 mb-6" data-id="element-313">
                <motion.button whileHover={{
                scale: 1.05
              }} whileTap={{
                scale: 0.95
              }} onClick={() => playNote(quizContent.audioA, 1)} className="px-8 py-4 bg-blue-100 text-blue-700 rounded-xl font-bold hover:bg-blue-200" data-id="element-314">
                  🔊 播放音A
                </motion.button>
                <motion.button whileHover={{
                scale: 1.05
              }} whileTap={{
                scale: 0.95
              }} onClick={() => playNote(quizContent.audioB, 1)} className="px-8 py-4 bg-purple-100 text-purple-700 rounded-xl font-bold hover:bg-purple-200" data-id="element-315">
                  🔊 播放音B
                </motion.button>
              </div>

              <div className="space-y-3" data-id="element-316">
                {quizContent.options.map((option: string, i: number) => <button key={i} onClick={() => !showResult && handleQuizAnswer(i)} disabled={showResult} className={`w-full p-4 rounded-xl border-2 font-medium text-left transition-all ${showResult ? i === quizContent.correctAnswer ? 'border-green-500 bg-green-50 text-green-700' : selectedAnswer === i ? 'border-red-500 bg-red-50 text-red-700' : 'border-slate-200 text-slate-400' : 'border-slate-200 hover:border-blue-300 hover:bg-blue-50'}`} data-id="element-317">
                    {option}
                    {showResult && i === quizContent.correctAnswer && ' ✓'}
                    {showResult && selectedAnswer === i && i !== quizContent.correctAnswer && ' ✗'}
                  </button>)}
              </div>
            </Card>

            {showResult && <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} data-id="element-318">
                <Card className={`p-6 ${selectedAnswer === quizContent.correctAnswer ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`} data-id="element-319">
                  <h4 className={`font-bold mb-2 ${selectedAnswer === quizContent.correctAnswer ? 'text-green-800' : 'text-amber-800'}`} data-id="element-320">
                    {selectedAnswer === quizContent.correctAnswer ? '🎉 回答正确！' : '💪 继续加油！'}
                  </h4>
                  <p className={selectedAnswer === quizContent.correctAnswer ? 'text-green-700' : 'text-amber-700'} data-id="element-321">
                    {quizContent.explanation}
                  </p>
                </Card>
              </motion.div>}
          </div>;
      case 'practice':
        const practiceContent = currentLesson.content as any;
        return <div className="space-y-6" data-id="element-322">
            <Card className="p-6 bg-green-50 border-green-100" data-id="element-323">
              <p className="text-green-700 font-medium text-center" data-id="element-324">
                {practiceContent.instruction}
              </p>
            </Card>

            <div className="grid grid-cols-4 gap-3" data-id="element-325">
              {practiceContent.scale.map((note: any, i: number) => <motion.button key={i} whileHover={{
              scale: 1.1,
              y: -5
            }} whileTap={{
              scale: 0.9
            }} onClick={() => playNote(note.freq, 0.6)} className={`aspect-square rounded-2xl font-bold text-white shadow-lg ${['bg-red-500', 'bg-orange-500', 'bg-yellow-500', 'bg-green-500', 'bg-teal-500', 'bg-blue-500', 'bg-purple-500', 'bg-pink-500'][i]}`} data-id="element-326">
                  <div className="text-2xl" data-id="element-327">{note.name}</div>
                </motion.button>)}
            </div>

            <button onClick={() => {
            practiceContent.scale.forEach((note: any, i: number) => {
              setTimeout(() => playNote(note.freq, 0.4), i * 400);
            });
          }} className="w-full py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-bold shadow-lg" data-id="element-328">
              🎵 自动播放完整音阶
            </button>
          </div>;
      case 'summary':
        const summaryContent = currentLesson.content as any;
        return <div className="space-y-6" data-id="element-329">
            <Card className="p-6 bg-gradient-to-br from-blue-50 to-purple-50" data-id="element-330">
              <div className="text-center mb-6" data-id="element-331">
                <div className="text-6xl mb-4" data-id="element-332">🎓</div>
                <h3 className="text-2xl font-bold text-slate-800" data-id="element-333">
                  恭喜完成学习！
                </h3>
                <p className="text-slate-500" data-id="element-334">你已经掌握了音高的基础知识</p>
              </div>

              <div className="space-y-4" data-id="element-335">
                {summaryContent.points.map((point: any, i: number) => <motion.div key={i} initial={{
                opacity: 0,
                x: -20
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: i * 0.15
              }} className="flex items-center space-x-4 p-4 bg-white rounded-xl shadow-sm" data-id="element-336">
                    <span className="text-2xl" data-id="element-337">{point.emoji}</span>
                    <span className="text-slate-700 font-medium" data-id="element-338">
                      {point.text}
                    </span>
                  </motion.div>)}
              </div>
            </Card>

            <Card className="p-6 bg-amber-50 border-amber-200" data-id="element-339">
              <div className="flex items-center justify-between" data-id="element-340">
                <div data-id="element-341">
                  <h4 className="font-bold text-amber-800" data-id="element-342">本次学习得分</h4>
                  <p className="text-amber-600 text-sm" data-id="element-343">
                    继续努力，争取更高分！
                  </p>
                </div>
                <div className="flex items-center space-x-2" data-id="element-344">
                  <Star className="text-amber-500" fill="currentColor" data-id="element-345" />
                  <span className="text-3xl font-bold text-amber-700" data-id="element-346">
                    {score}
                  </span>
                </div>
              </div>
            </Card>
          </div>;
      default:
        return null;
    }
  };
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="min-h-screen w-full flex flex-col bg-slate-50" data-id="element-347">
      {/* Header */}
      <div className="bg-white border-b border-slate-100 sticky top-0 z-50" data-id="element-348">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between" data-id="element-349">
          <button onClick={onBack} className="flex items-center space-x-2 text-slate-600 hover:text-slate-900 bg-slate-50 px-4 py-2 rounded-full" data-id="element-350">
            <ArrowLeft size={20} data-id="element-351" />
            <span className="font-bold text-sm" data-id="element-352">返回</span>
          </button>

          <h1 className="text-xl font-bold text-slate-900" data-id="element-353">音高训练</h1>

          <div className="flex items-center space-x-2 bg-amber-50 px-4 py-2 rounded-full" data-id="element-354">
            <Star className="text-amber-500" size={18} fill="currentColor" data-id="element-355" />
            <span className="font-bold text-amber-700" data-id="element-356">{score}</span>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="max-w-4xl mx-auto px-4 pb-4" data-id="element-357">
          <div className="flex items-center space-x-2" data-id="element-358">
            {lessonSteps.map((step, i) => <div key={i} className="flex-1 flex items-center" data-id="element-359">
                <div className={`w-full h-2 rounded-full ${completedSteps.includes(i) ? 'bg-green-500' : i === currentStep ? 'bg-blue-500' : 'bg-slate-200'}`} data-id="element-360" />
              </div>)}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 p-4 md:p-8" data-id="element-361">
        <div className="max-w-2xl mx-auto" data-id="element-362">
          {/* Step Header */}
          <div className="mb-6 text-center" data-id="element-363">
            <div className="inline-flex items-center space-x-2 bg-white px-4 py-2 rounded-full shadow-sm mb-4" data-id="element-364">
              <span className="text-2xl" data-id="element-365">{currentLesson.icon}</span>
              <span className="text-sm font-medium text-slate-500" data-id="element-366">
                第 {currentStep + 1} 步 / 共 {lessonSteps.length} 步
              </span>
            </div>
            <h2 className="text-2xl font-bold text-slate-900" data-id="element-367">
              {currentLesson.title}
            </h2>
          </div>

          {/* Content */}
          <AnimatePresence mode="wait" data-id="element-368">
            <motion.div key={currentStep} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -20
          }} transition={{
            duration: 0.3
          }} data-id="element-369">
              {renderStepContent()}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Footer Navigation */}
      <div className="bg-white border-t border-slate-100 p-4" data-id="element-370">
        <div className="max-w-2xl mx-auto flex justify-between items-center" data-id="element-371">
          <button onClick={() => {
          setSelectedAnswer(null);
          setShowResult(false);
          if (currentStep > 0) setCurrentStep(currentStep - 1);
        }} disabled={currentStep === 0} className="px-6 py-2 rounded-full border border-slate-200 text-slate-600 font-medium disabled:opacity-50" data-id="element-372">
            上一步
          </button>

          <button onClick={currentStep === lessonSteps.length - 1 ? onBack : handleNextStep} className="px-8 py-2 rounded-full bg-blue-600 text-white font-bold shadow-md hover:bg-blue-700" data-id="element-373">
            {currentStep === lessonSteps.length - 1 ? '完成学习' : '下一步'}
            {currentStep < lessonSteps.length - 1 && <ChevronRight className="inline ml-1" size={18} data-id="element-374" />}
          </button>
        </div>
      </div>
    </motion.div>;
}